import { clientLog } from 'utils/logger';
import { EventEmitter } from 'common/utils/event-emitter';
import { storyEmbedEvents, STORY_MESSAGE_SENDER_ID } from 'client/constants/common';
import store from 'client/store';
import { setEventBridgeLoading } from 'client/actions/set-event-bridge-loading';
import { GAEventAdapter } from './ga-event-adapter';
import { ApiEventAdapter } from './api-event-adapter';
import { CustomEventAdapter } from './custom-event-adapter';
import { BRIDGE_EVENT_TYPE } from './types';
import type { IEventBridgeAdapter, BridgeEvent, GAEvent, ApiEvent, CustomEvent } from './types';

const log = clientLog.extend('event-bridge');

const interceptorConfig = (() => {
	let _isLoadingDisabled = false;

	return {
		loadingTimeout: null as NodeJS.Timeout | null,
		loadingDelay: 150,
		isLoaderShown: false,
		get isLoadingDisabled(): boolean {
			return _isLoadingDisabled;
		},
		set isLoadingDisabled(value: boolean) {
			_isLoadingDisabled = value;
			if (value && this.loadingTimeout !== null) {
				log('🔄 Clearing timeout due to loading being disabled');
				clearTimeout(this.loadingTimeout);
				this.loadingTimeout = null;
			}
		},
	};
})();

/**
 * EventBridgeLoader - Controls loading indicator visibility
 */
const EventBridgeLoader: StoryCardsAPI['ContextLoader'] = {
	disable: () => {
		log('🚫 Disabling loader');
		interceptorConfig.isLoadingDisabled = true;
	},
	enable: () => {
		log('✅ Enabling loader');
		interceptorConfig.isLoadingDisabled = false;
	},
	isDisabled: () => interceptorConfig.isLoadingDisabled,
	hide: () => {
		log('🔄 Hiding loader (manual)');
		store.dispatch(setEventBridgeLoading(false));
		interceptorConfig.isLoaderShown = false;
	},
	show: () => {
		log('🔄 Showing loader (manual)');
		store.dispatch(setEventBridgeLoading(true));
		interceptorConfig.isLoaderShown = true;
	},
};

const EventBridgeEmitter = new EventEmitter<BridgeEvent>({
	context: {
		loader: EventBridgeLoader,
	},
	onInterceptorStateChange: isPending => {
		// Skip loader if disabled
		if (interceptorConfig.isLoadingDisabled) {
			log('🚫 Loader disabled, skipping loader logic');
			return;
		}

		if (isPending && interceptorConfig.loadingTimeout === null) {
			log('🔄 Starting timeout for loader');
			interceptorConfig.loadingTimeout = setTimeout(() => {
				log('🔄 Showing loader after delay');
				store.dispatch(setEventBridgeLoading(true));
				interceptorConfig.isLoaderShown = true;
				interceptorConfig.loadingTimeout = null;
			}, interceptorConfig.loadingDelay);
		} else {
			if (interceptorConfig.loadingTimeout !== null) {
				log('🔄 Clear timeout');
				clearTimeout(interceptorConfig.loadingTimeout);
				interceptorConfig.loadingTimeout = null;
			}
			if (interceptorConfig.isLoaderShown) {
				log('🔄 Hiding loader');
				store.dispatch(setEventBridgeLoading(false));
				interceptorConfig.isLoaderShown = false;
			}
		}
	},
});

class EventBridge<OriginalEvent, FormattedEvent = OriginalEvent> {
	private _adapter: IEventBridgeAdapter<OriginalEvent, FormattedEvent>;
	private _eventEmitter: EventEmitter<BridgeEvent>;

	constructor(adapter: IEventBridgeAdapter<OriginalEvent, FormattedEvent>) {
		this._adapter = adapter;
		this._eventEmitter = EventBridgeEmitter;
	}

	sendEvent = async (e: OriginalEvent): Promise<void> => {
		const formattedEvent = this._adapter.formatEvent(e);

		if (!formattedEvent.originalEvent) {
			return;
		}

		const originalEvent = formattedEvent.originalEvent as unknown as ApiEvent | GAEvent | CustomEvent;
		const event: BridgeEvent = {
			type: BRIDGE_EVENT_TYPE,
			data: {
				type: originalEvent.type,
				details: originalEvent.details,
				timestamp: formattedEvent.timestamp,
				category: formattedEvent.category,
			},
		};

		log('📤 sendEvent', event);

		// Send to parent window (existing embed functionality)
		window.parent.postMessage(
			{
				type: storyEmbedEvents.EMBED_BRIDGE,
				sender: STORY_MESSAGE_SENDER_ID,
				payload: event,
			},
			'*'
		);

		// Emit locally so internal listeners can respond to the event
		// This allows other components to react to events via EventBridgeEmitter.on()
		await this._eventEmitter.emit(event.type, event.data);
	};
}

const gaBridge = new EventBridge(new GAEventAdapter());

const apiBridge = new EventBridge(new ApiEventAdapter());

const customBridge = new EventBridge(new CustomEventAdapter());

export {
	EventBridgeEmitter,
	EventBridge,
	EventBridgeLoader,
	gaBridge,
	apiBridge,
	customBridge,
	BRIDGE_EVENT_TYPE,
	IEventBridgeAdapter,
	BridgeEvent,
	GAEvent,
	ApiEvent,
	CustomEvent,
};
